import React from 'react';
import clsx from 'clsx';
import { makeStyles, Theme } from '@material-ui/core/styles';
import SpeedIcon from '../icons/SpeedIcon';
import HuddleVisionIcon from '../icons/HuddleVisionIcon';
import RedCircle from '../icons/RedCircleIcon';
import EnduranceIcon from '../icons/EnduranceIcon';
import ListItem from '../atoms/ListItem';
import NamedBox from '../atoms/NamedBox';
import ProgressLine from '../atoms/ProgressLine';
import Tooltip from '../atoms/Tooltip';
import InGameRating from '../types/InGameRating';
import {
  COLOR_BLACK,
  COLOR_BLUE,
  COLOR_DARK_BLUE,
  COLOR_GREEN,
  COLOR_PURPLE,
  COLOR_WHITE,
} from '../styles/colors';
import CircleBadge from '../atoms/CircleBadge';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import CheckIcon from '../icons/CheckIcon';
import EllipsisIcon from '../icons/EllipsisIcon';
import { Grid } from '@material-ui/core';
import SpeedRecruitIcon from '../icons/SpeedRecruitIcon';
import VideoIcon from '@material-ui/icons/PlayCircleOutline';

interface PlayerInGameRatingProps {
  className?: string;
  inGameRating: InGameRating;
  label?: string;
}

const BETTER_THAN_TOOLTIP_TEXT = 'Better than D-I position';

const useStyles = makeStyles((theme: Theme) => ({
  PlayerInGameRating: {},

  progressLineWrapper: {
    width: '100%',
  },
  progressLine: {
    maxWidth: 350,
    paddingRight: 0,
    marginLeft: 'auto',
    cursor: 'help',
  },
  link: {
    cursor: 'pointer',
    color: COLOR_BLUE,

    '&:hover': {
      color: COLOR_DARK_BLUE,
      textDecoration: 'underline',
    }
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    alignItems: 'center',
  },
  badgeWrap: {
    marginRight: 10,
    width: '45px',
  },
  badgeVal: {
    color: COLOR_BLACK,
  },
  iconCell: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'end',
  },
  icon: {
    width: '50%',
    height: '50%',
  },
  fullWidth: {
    width: '100%',
  },
  infoIcon: {
    width: 20,
    height: 20,
  },
  checkIcon: {
    backgroundColor: COLOR_GREEN,
    border: `3px solid ${COLOR_GREEN}`,
    borderRadius: '50%',
    color: COLOR_WHITE,
    height: '24px',
    padding: '1px',
    width: '24px',
  },
  speedRecuitIconWrap: {
    marginRight: '8px',
    [theme.breakpoints.between(1260, 1440)]: {
      marginRight: '12px',
    },
  },
  ellipsis: {
    // maxWidth: '120px',
    minWidth: '80px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  }
}), { name: PlayerInGameRating.name });

export default function PlayerInGameRating (props: PlayerInGameRatingProps) {
  const {
    className,
    inGameRating,
    label,
  } = props;
  const classes = useStyles();
  const Icon:any = HuddleVisionIcon;

  if (!inGameRating) return null;

  function formatToTenth(value: number): string {
    // Format the number to one decimal place using toFixed(1)
    return value.toFixed(1);
  }

  function formatToHundredth(value: number): string {
    // Format the number to two decimal place using toFixed(1)
    return value.toFixed(2);
  }

  const noEvents = !(inGameRating?.MPH || inGameRating?.MPHPercentage || inGameRating?.timeToMax);
  if (noEvents) return null;
  const mphPercentageValue = inGameRating?.MPHPercentage && formatToTenth(inGameRating?.MPHPercentage);
  const mphConfidencePercentValue = inGameRating?.mphConfidencePercent && formatToTenth(inGameRating?.mphConfidencePercent);

  function isSpeedRecruitEnable (value: any):boolean {
    if (value >= 75) {
      return true;
    }
    return false;
  }

  return (
    <NamedBox
      className={clsx(classes.PlayerInGameRating, className)}
      headerNameClassName={classes.fullWidth}
      name={
        <div className={classes.header}>
          <CircleBadge
            className={classes}
            value={'MPH'}
            height={45} 
            width={45}
            color={COLOR_PURPLE}
          />
          <span style={{flexGrow: '1'}}>{label || 'In-Game Metrics'} </span>
            <div className={classes.iconCell}>
              <a className={classes.iconCell} href='https://huddlevision.ai' target='_blank' rel='noopener noreferrer'>
                  <Icon className={clsx(classes.icon)} />
                  <div style={{paddingBottom: '3px'}}>
                    <Tooltip title='All in-game and mph data created in collaboration with Huddlevision.ai and Tracking Football technology.'>
                      <InfoOutlinedIcon className={classes.infoIcon} />
                    </Tooltip>
                  </div>
              </a>
            </div>
        </div>
      }
    >
      {(inGameRating?.MPH && inGameRating?.MPHPercentage)
          ? <ListItem
              icon={SpeedIcon}
              // title={`Max Speed (MPH):`}
              title={<><span>Max Speed</span><span style={{fontSize: '12px'}}> (MPH):</span></>}
              // extendWith={true}
            >
              <Grid item lg={1} xs={2}></Grid>
              <Grid item lg={3} xs={5}>
              <b>{inGameRating?.MPH ? formatToHundredth(inGameRating?.MPH) : 'N/A'}</b>
              </Grid>
              {/* <Tooltip title={BETTER_THAN_TOOLTIP_TEXT}>
                <div className={classes.progressLineWrapper}>
                  <ProgressLine
                    className={classes.progressLine}
                    value={inGameRating?.MPHPercentage}
                    showPercentileSign={false}
                  />
                </div>
              </Tooltip> */}
            </ListItem>
          : null
          }

          {/* {(inGameRating?.timeToMax)
          ? <ListItem
              icon={SpeedIcon}
              title='Time to Max Speed (Sec):'
              // extendWith={true}
            >
              <b>{formatToTenth(inGameRating?.timeToMax)}</b>
            </ListItem>
            : null
          } */}

          {(inGameRating?.distanceTraveled)
          ? <ListItem
              icon={EnduranceIcon}
              title='Play Distance:'
              // extendWith={true}
            >
              <Grid item lg={1} xs={2}></Grid>
              <Grid item lg={3} xs={5}>
              <b>{formatToTenth(inGameRating?.distanceTraveled)} yds.</b>
              </Grid>
            </ListItem>
            : null
          }

          {(inGameRating?.MPHPercentage)
          ? <ListItem
              icon={CheckIcon}
              iconClassName={classes.checkIcon}
              title='MPH Database:'
              // extendWith={true}
            >
              <Grid container>
              {/* <Grid item lg={1} xs={2}></Grid>
              <Grid item lg={3} xs={5}>
              <b>{formatToTenth(inGameRating?.distanceTraveled)} yds.</b>
              </Grid> */}
                <Grid lg={1} xs={2}>
                {isSpeedRecruitEnable(mphPercentageValue) && (
                    <SpeedRecruitIcon className={'purple'} />
                  )}
                </Grid>
                <Grid lg={3} xs={5}>
                  <b>{mphPercentageValue}%</b>
                </Grid>
                <Grid item lg xs>
                  <div className={classes.progressLineWrapper}>
                    <ProgressLine
                      className={classes.progressLine}
                      value={inGameRating?.MPHPercentage}
                    />
                  </div>
                </Grid>
              </Grid>
            </ListItem>
            : null
          }

          {(inGameRating?.mphConfidencePercent)
          ? <ListItem
              icon={EllipsisIcon}
              title='MPH Confidence:'
              // extendWith={true}
            >
              <Grid item lg={1} xs={2}></Grid>
              <Grid item lg={3} xs={5}>
              <b>{mphConfidencePercentValue}%</b>
              </Grid>
            </ListItem>
            : null
          }

          {(inGameRating?.year)
          ? <ListItem
              icon={VideoIcon}
              title='HS Film Year:'
              // extendWith={true}
            >
              <Grid item lg={1} xs={2}></Grid>
              <Grid item lg={3} xs={5}>
              <b>{inGameRating?.year}</b>
              </Grid>
            </ListItem>
            : null
          }

          {(inGameRating?.filmUrl)
          ? <ListItem
              icon={VideoIcon}
              title='HS Film URL:'
              // extendWith={true}
            >
              <Grid item lg={1} xs={2}></Grid>
              <Grid item lg={11} xs={11} className={classes.ellipsis}>
              <b>
                <a
                  className={classes.link}
                  href={inGameRating?.filmUrl}
                  target='_blank'
                  title={inGameRating?.filmUrl}
                >
                  {inGameRating?.filmUrl}
                </a>
              </b>
              </Grid>
            </ListItem>
            : null
          }
    </NamedBox>
  );
}
